/**
 * @file src/context/auth.tsx
 * @description Auth context — JWT storage, login, register, logout, refresh token rotation
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'

interface AuthState {
  token:  string | null
  userId: string | null
  plan:   string | null
  email:  string | null
}

interface AuthContextValue extends AuthState {
  login:    (email: string, password: string) => Promise<void>
  register: (email: string, password: string, inviteCode: string, plan?: string) => Promise<void>
  logout:   () => void
  isAuthenticated: boolean
  isRestoring: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY   = 'jarvis_token'
const USER_KEY    = 'jarvis_user'
const REFRESH_KEY = 'spark_refresh_token'

function loadStored(): AuthState {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY)
    const user  = JSON.parse(sessionStorage.getItem(USER_KEY) ?? 'null')
    return {
      token,
      userId: user?.id    ?? null,
      plan:   user?.plan  ?? null,
      email:  user?.email ?? null,
    }
  } catch {
    return { token: null, userId: null, plan: null, email: null }
  }
}

function persist(
  accessToken: string,
  user: { id: string; email: string; plan: string },
  refreshToken?: string
) {
  sessionStorage.setItem(TOKEN_KEY, accessToken)
  sessionStorage.setItem(USER_KEY, JSON.stringify(user))
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken)
  }
}

function clearAll() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(USER_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

async function apiFetch(path: string, body: unknown) {
  const res = await fetch(path, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as Record<string, string>
    throw new Error(data['message'] ?? `Request failed: ${res.status}`)
  }
  return res.json() as Promise<{
    accessToken:  string
    refreshToken?: string
    user: { id: string; email: string; plan: string }
  }>
}

async function performSilentRefresh(): Promise<{ accessToken: string; user: { id: string; email: string; plan: string } } | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return null
  try {
    const res = await fetch('/v1/auth/refresh', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refreshToken }),
    })
    if (!res.ok) {
      localStorage.removeItem(REFRESH_KEY)
      return null
    }
    const data = await res.json() as {
      accessToken: string
      refreshToken: string
      user?: { id: string; email: string; plan: string }
    }
    // 存新 refresh token（rotation）
    localStorage.setItem(REFRESH_KEY, data.refreshToken)

    // 从 JWT payload 解析用户信息（如果后端没返回 user）
    let user = data.user
    if (!user) {
      try {
        const payload = JSON.parse(atob(data.accessToken.split('.')[1]!))
        user = { id: payload.sub, email: payload.email, plan: payload.plan ?? 'free' }
      } catch {
        return null
      }
    }

    sessionStorage.setItem(TOKEN_KEY, data.accessToken)
    sessionStorage.setItem(USER_KEY, JSON.stringify(user))
    return { accessToken: data.accessToken, user }
  } catch {
    return null
  }
}

// 用 refresh token 静默续期。Web Lock 避免多个同源标签页同时轮换同一个 token。
async function silentRefresh(): Promise<{ accessToken: string; user: { id: string; email: string; plan: string } } | null> {
  if ('locks' in navigator) {
    return navigator.locks.request('spark-auth-refresh', performSilentRefresh)
  }
  return performSilentRefresh()
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadStored)
  const [isRestoring, setIsRestoring] = useState(
    () => !sessionStorage.getItem(TOKEN_KEY) && Boolean(localStorage.getItem(REFRESH_KEY)),
  )
  const restoreAttempted = useRef(false)

  // 页面加载时：sessionStorage 无 token → 尝试 refresh token 静默续期
  useEffect(() => {
    if (restoreAttempted.current) return
    restoreAttempted.current = true

    if (state.token) {
      setIsRestoring(false)
      return
    }
    const refreshToken = localStorage.getItem(REFRESH_KEY)
    if (!refreshToken) {
      setIsRestoring(false)
      return
    }

    void silentRefresh()
      .then(result => {
        if (result) {
          setState({
            token:  result.accessToken,
            userId: result.user.id,
            plan:   result.user.plan,
            email:  result.user.email,
          })
        }
      })
      .finally(() => setIsRestoring(false))
  }, [])

  // access token 即将过期时自动续期（每5分钟检查）
  useEffect(() => {
    if (!state.token) return

    const checkExpiry = () => {
      try {
        const payload = JSON.parse(atob(state.token!.split('.')[1]!))
        const exp = payload.exp * 1000
        const now = Date.now()
        const remaining = exp - now

        // 剩余不足 1 小时时静默刷新
        if (remaining < 60 * 60 * 1000) {
          silentRefresh().then(result => {
            if (result) {
              setState(prev => ({ ...prev, token: result.accessToken }))
            }
          })
        }
      } catch { /* ignore */ }
    }

    checkExpiry()
    const interval = setInterval(checkExpiry, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [state.token])

  const login = useCallback(async (email: string, password: string) => {
    const { accessToken, refreshToken, user } = await apiFetch('/v1/auth/login', { email, password })
    persist(accessToken, user, refreshToken)
    setState({ token: accessToken, userId: user.id, plan: user.plan, email: user.email })
  }, [])

  const register = useCallback(async (
    email: string,
    password: string,
    inviteCode: string,
    plan = 'free',
  ) => {
    const { accessToken, refreshToken, user } = await apiFetch('/v1/auth/register', {
      email, password, inviteCode, plan,
    })
    persist(accessToken, user, refreshToken)
    setState({ token: accessToken, userId: user.id, plan: user.plan, email: user.email })
  }, [])

  const logout = useCallback(() => {
    clearAll()
    setState({ token: null, userId: null, plan: null, email: null })
  }, [])

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      register,
      logout,
      isAuthenticated: !!state.token,
      isRestoring,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
